/**
 * Correction spans (M6c step 4) — the pure core of the journal and of chat corrections.
 *
 * ONE implementation, shared by both sides, like `lib/tokenize.ts`: the worker locates a span when
 * a check comes back, the client re-anchors it on every keystroke after that, and both must agree
 * about what "this span" means down to the character. This module is aliased into the web bundle
 * as `@nelnela/corrections` for exactly that reason — the standing warning is M6a's dictation
 * tint, where a punctuation class copied into `Review.tsx` drifted by ONE code point (a literal
 * U+0020 where the worker had U+2000) and silently killed the feature for a whole milestone, with
 * the two source lines indistinguishable on screen.
 *
 * Zero imports, so it can live in both bundles.
 *
 * OFFSETS are JavaScript string indices (UTF-16 code units) over the NFC-normalised text — the
 * convention `lib/tokenize.ts` states and pins. Georgian Mkhedruli is entirely in the BMP, so for
 * the Phase-1 pair they are also code points; the normalisation matters for the English word or
 * the emoji a journal will eventually contain, and it is a no-op for Georgian.
 *
 * THE MODEL NEVER SENDS OFFSETS. It quotes the span it means and this file finds it. A model asked
 * for `char_start` returns a number that is confidently wrong by two, and an underline on the wrong
 * word is a visible lie about the learner's own sentence.
 */

/** The one normalisation both sides apply before anything else touches the text. */
export function normalizeText(text: string): string {
  return text.normalize('NFC');
}

export interface Span {
  charStart: number;
  charEnd: number;
}

/**
 * Every occurrence of `needle` in `haystack`, by index. Plain `indexOf` scanning rather than a
 * RegExp: the needle is learner text and would need escaping, and a mis-escaped `.` in a Georgian
 * sentence matches the wrong word rather than failing loudly.
 */
export function occurrences(haystack: string, needle: string): number[] {
  if (needle.length === 0) return [];
  const out: number[] = [];
  for (let i = haystack.indexOf(needle); i !== -1; i = haystack.indexOf(needle, i + 1)) out.push(i);
  return out;
}

/**
 * Where the model's quoted span actually is, or `null`.
 *
 * The rules, in order, and the last one is the important one:
 *   1. one occurrence — that is the span;
 *   2. several occurrences and a `contextBefore` — the occurrence whose preceding text ends with
 *      it wins, and only if exactly one does;
 *   3. anything else — **null, and the correction is dropped.**
 *
 * Rule 3 is the whole design. `lib/exercises.ts`'s `locateSurface` returns null rather than
 * guessing for the same reason, and `toStoryItemRows` drops a tag whose index it cannot trust
 * rather than putting a mark on the wrong sentence. A dropped correction costs the learner one
 * piece of advice; a guessed one puts a red underline under a word that is correct, in their own
 * diary, with an explanation of a mistake they did not make.
 *
 * `contextBefore` is matched by SUFFIX, not by equality: the model is asked for "up to 24
 * characters immediately before" and will trim, pad or shorten it, and a suffix test is true for
 * every honest version of that.
 */
export function locateCorrection(text: string, original: string, contextBefore = ''): Span | null {
  const hay = normalizeText(text);
  const needle = normalizeText(original);
  const hits = occurrences(hay, needle);
  if (hits.length === 0) return null;
  if (hits.length === 1) return { charStart: hits[0]!, charEnd: hits[0]! + needle.length };

  const ctx = normalizeText(contextBefore);
  if (ctx.length === 0) return null;
  const matched = hits.filter((i) => hay.slice(0, i).endsWith(ctx));
  if (matched.length !== 1) return null;
  return { charStart: matched[0]!, charEnd: matched[0]! + needle.length };
}

/** How far from its old position `reanchor` will look before it searches the whole text. */
export const REANCHOR_WINDOW = 120;

/** What became of a stored correction when the draft moved on under it. */
export type AnchorState =
  /** the span still holds, or was found again — draw the underline */
  | 'anchored'
  /** the learner took the fix: `suggested` is sitting where `original` was — draw nothing */
  | 'resolved'
  /** the text it referred to is gone or ambiguous — fade it, and give it no tap target */
  | 'stale';

export interface AnchorInput {
  charStart: number;
  charEnd: number;
  original: string;
  suggested: string;
}
export interface Anchored<T> {
  correction: T;
  state: AnchorState;
  /** null when `state` is not 'anchored' */
  span: Span | null;
}

/**
 * A letter, a combining mark or a digit — i.e. a character a WORD carries on through.
 *
 * Unicode properties rather than a Georgian range, for the same reason `normalizeText` is applied
 * to text that cannot contain a combining mark: a journal is Georgian with an English word, a
 * brand and a date in it, and a rule that only knew Mkhedruli would treat `Google` as punctuation.
 * `\p{M}` is in the class deliberately — a combining mark after the last letter of a match is that
 * letter continuing, not the next word starting.
 */
const WORD_CHAR = /[\p{L}\p{M}\p{N}]/u;

/**
 * Does a match of `needle` at `at` stop where a word stops, on both sides?
 *
 * **The bug this exists to prevent.** A correction of `ვჭამ` → `ვჭამე` that the learner answers by
 * rewriting the verb as `ვჭამდი` left `original` still matching at the stored offset — as a PREFIX
 * of a word that no longer exists — so `reanchor` reported it `anchored`, the mirror drew the error
 * tint under the first four letters of the new word, and Accept spliced the suggestion over them
 * and produced `მე ვჭამედი ხილს`. The learner's own sentence, corrupted by the app, on the path
 * `onEdit` deliberately sets up (it parks the caret at `span.charEnd` so the ending can be changed).
 * The honest answer is that the text the correction was about is gone: fall through to `stale` and
 * fade. A faded mark costs the learner one piece of advice; this cost them a false accusation and
 * a corrupting Accept.
 *
 * The test is `\b`'s and not "surrounded by spaces": an edge only has to be a boundary when the
 * needle's own edge character is a word character, so a quote at index 0, one ending the draft and
 * one hard up against a comma all still anchor.
 *
 * **The accepted cost.** The prompt asks the model for "the smallest span that carries" a mistake,
 * so a correction quoting part of a word — a bare suffix — is permitted by it, and this rule makes
 * such a correction unplaceable rather than mis-placeable: it goes `stale` immediately and every
 * time. That is a real loss, taken deliberately, because there is no information in
 * (text, charStart, original, suggested) that tells a legitimate sub-word quote apart from a word
 * the learner has since extended — only the version text would, and this function does not have
 * it. Every correction in the milestone's prompt, design and fixtures quotes a whole word or
 * phrase, and a mark on the wrong letters of a word in a private diary is the one failure this
 * screen is not allowed.
 */
function stopsAtWordEdge(text: string, needle: string, at: number): boolean {
  const first = needle[0];
  const last = needle[needle.length - 1];
  if (first !== undefined && WORD_CHAR.test(first)) {
    const before = text[at - 1];
    if (before !== undefined && WORD_CHAR.test(before)) return false;
  }
  if (last !== undefined && WORD_CHAR.test(last)) {
    const after = text[at + needle.length];
    if (after !== undefined && WORD_CHAR.test(after)) return false;
  }
  return true;
}

/**
 * Re-place stored corrections against the draft as it is NOW.
 *
 * Design `8d`'s caption promises that "stale corrections fade rather than mis-underline", and this
 * function is that promise. A correction is made against a `composition_version`; the learner keeps
 * typing; every character they insert before a span moves it.
 *
 * At the old position, **the longest match wins**, and that rule is the whole of step 1.
 *
 * The first version of this function tested `resolved` before `anchored` — reasoning that
 * `suggested` often contains `original` (`მოვედი` → `მოვედით`), so testing `original` first would
 * re-underline a word the learner had just fixed. That reasoning was right and the implementation
 * was still wrong, because the containment runs BOTH ways: design `8d`'s own correction is
 * `ავდგები` → `ავდგე`, where `suggested` is a PREFIX of `original`, so on a completely unedited
 * draft `startsWith(suggested, charStart)` is true and the correction reported itself resolved
 * before it had ever been shown. Measured: the design's canonical example never drew an underline
 * at all. Whichever of the two strings is actually sitting there is the longer one — a prefix
 * match is an accident of one being a truncation of the other — so length decides, and both
 * directions of containment come out right.
 *
 * Then, in order:
 *   2. a unique occurrence within ±`REANCHOR_WINDOW` of where it was — the common case, since an
 *      edit somewhere else in the paragraph shifts everything after it by a few characters;
 *   3. a unique occurrence anywhere;
 *   4. stale.
 *
 * Steps 2 and 3 demand UNIQUENESS inside their scope. Two candidates and no way to choose is the
 * mis-underline this function exists to prevent, so it fades instead.
 *
 * EVERY match, at the old position and in both scans, must also stop at a word edge
 * (`stopsAtWordEdge`) — otherwise a word the learner has extended keeps the old underline on its
 * prefix. That check is where a match that is only half a word goes to die.
 */
export function reanchor<T extends AnchorInput>(
  corrections: readonly T[],
  currentText: string,
): Array<Anchored<T>> {
  const text = normalizeText(currentText);
  return corrections.map((c) => {
    const original = normalizeText(c.original);
    const suggested = normalizeText(c.suggested);

    // Longest match at the span wins — see the note above about `ავდგები` → `ავდგე` — and a match
    // that runs on into a longer word is not a match at all (`stopsAtWordEdge`). Note what the
    // second rule does to the `მოვედი` → `მოვედით` case: `original` still starts at the offset but
    // is followed by თ, so only `suggested` matches and the correction reads `resolved` — the same
    // answer the longest-match rule gave, reached one step earlier.
    const hasOriginal =
      original.length > 0 &&
      text.startsWith(original, c.charStart) &&
      stopsAtWordEdge(text, original, c.charStart);
    const hasSuggested =
      suggested.length > 0 &&
      text.startsWith(suggested, c.charStart) &&
      stopsAtWordEdge(text, suggested, c.charStart);
    if (hasOriginal || hasSuggested) {
      const originalWins = hasOriginal && (!hasSuggested || original.length > suggested.length);
      if (!originalWins) return { correction: c, state: 'resolved' as const, span: null };
      return {
        correction: c,
        state: 'anchored' as const,
        span: { charStart: c.charStart, charEnd: c.charStart + original.length },
      };
    }

    // The scans see only whole-word hits, so a paragraph containing both ვჭამ and ვჭამდი offers
    // one candidate rather than two — the filter makes the uniqueness test below sharper, not
    // blunter.
    const hits = occurrences(text, original).filter((i) => stopsAtWordEdge(text, original, i));
    if (hits.length === 0) return { correction: c, state: 'stale' as const, span: null };

    const near = hits.filter((i) => Math.abs(i - c.charStart) <= REANCHOR_WINDOW);
    const pick = near.length === 1 ? near[0]! : hits.length === 1 ? hits[0]! : null;
    if (pick === null) return { correction: c, state: 'stale' as const, span: null };
    return {
      correction: c,
      state: 'anchored' as const,
      span: { charStart: pick, charEnd: pick + original.length },
    };
  });
}

/** Does `[aStart, aEnd)` overlap `[bStart, bEnd)`? Touching ends do not overlap. */
export function overlaps(a: Span, b: Span): boolean {
  return a.charStart < b.charEnd && b.charStart < a.charEnd;
}

/**
 * Drop any span that overlaps one already kept, first-come.
 *
 * Two underlines on one word cannot both be tapped: the caret is in both, and the card would show
 * whichever the array happened to list first. The prompt asks the model not to overlap; this is
 * what makes that true rather than hoped for.
 */
export function dropOverlaps<T extends { span: Span }>(items: readonly T[]): T[] {
  const kept: T[] = [];
  for (const it of items) {
    if (kept.some((k) => overlaps(k.span, it.span))) continue;
    kept.push(it);
  }
  return kept;
}

/**
 * Which tagged items sit inside a corrected span, and which do not.
 *
 * This is the split that becomes `production_use`: an item inside a correction is a produce
 * FAILURE, an item the learner used cleanly is a produce SUCCESS. A tag that straddles the
 * boundary counts as inside — the correction touched it, so the app does not get to claim the
 * learner produced it correctly.
 */
export function partitionByCorrections<T extends Span>(
  tags: readonly T[],
  corrected: readonly Span[],
): { inside: T[]; outside: T[] } {
  const inside: T[] = [];
  const outside: T[] = [];
  for (const t of tags) {
    if (corrected.some((c) => overlaps(c, t))) inside.push(t);
    else outside.push(t);
  }
  return { inside, outside };
}
